import React from "react";
import { Check, Clock, DollarSign, Lock, MessageCircle, RefreshCw, Search, Trash2 } from "lucide-react";
import { UserStatusBadge } from "../shared/StatusBadges";
import { useAppContext } from "../../AppContext";
import { bsAmount } from "../../lib/bsFormat";

/* -----------------------------------------------------------------------------
 * UserManagement — admin "USUARIOS" tab. Client list + role/status filters +
 * suspend/block/unblock/delete actions. Extracted from AdminPage.jsx.
 * --------------------------------------------------------------------------- */
export function UserManagement() {
  const ctx = useAppContext();
  const {
    // Users
    users, loadingUsers, loadUsers, userRoleFilter, setUserRoleFilter,
    userSearch, setUserSearch, requestStatusChange,
    handleToggleUserRole, setDeleteUserConfirm,
    handleChangeUserStatus,
    user, // current admin (for self-guard when displaying actions)
  } = ctx;

  return (
              <div className="space-y-6" data-testid="admin-users-view">
                <div className="bg-ag7-surface border border-[var(--ag7-border-soft)] rounded-2xl p-6 flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-heading font-extrabold tracking-tight text-white mb-1">Gestión de Usuarios</h3>
                    <p className="text-xs text-zinc-400">Bloquea, suspende o elimina cuentas. Los pedidos históricos se preservan para auditoría.</p>
                  </div>
                  <button onClick={loadUsers} data-testid="refresh-users-btn"
                    className="bg-ag7-surface-2 hover:bg-zinc-700 text-white font-bold px-4 py-2 rounded-xl text-xs flex items-center gap-2">
                    <RefreshCw className={`w-3.5 h-3.5 ${loadingUsers ? "animate-spin" : ""}`} /> Actualizar
                  </button>
                </div>

                {/* Registration stats — total + breakdown by role + registrations in the last 30 days */}
                {(() => {
                  const now = Date.now();
                  const in30d = 30 * 24 * 60 * 60 * 1000;
                  const in7d = 7 * 24 * 60 * 60 * 1000;
                  const clients = users?.filter(u => u.role === "client").length;
                  const resellers = users?.filter(u => u.role === "reseller").length;
                  const admins = users?.filter(u => u.role === "admin").length;
                  const active = users?.filter(u => !u.is_blocked).length;
                  const blocked = users.length - active;
                  const last30 = users?.filter(u => u.created_at && (now - Date.parse(u.created_at)) < in30d).length;
                  const last7 = users?.filter(u => u.created_at && (now - Date.parse(u.created_at)) < in7d).length;
                  const cards = [
                    { id: "all", label: "Total Registrados", value: users.length, sub: `${active} activos · ${blocked} bloqueados`, accent: "cyan" },
                    { id: "client", label: "Clientes", value: clients, sub: "Rol: client", accent: "purple" },
                    { id: "reseller", label: "Mayoristas", value: resellers, sub: "Rol: reseller", accent: "amber" },
                    { id: "new30d", label: "Nuevos (30d)", value: last30, sub: `${last7} en últimos 7 días`, accent: "green" },
                  ];
                  const accentMap = {
                    cyan: { border: "border-cyan-500/40", text: "text-cyan-300", ring: "ring-cyan-400" },
                    purple: { border: "border-purple-500/40", text: "text-purple-300", ring: "ring-purple-400" },
                    amber: { border: "border-amber-500/40", text: "text-amber-300", ring: "ring-amber-400" },
                    green: { border: "border-green-500/40", text: "text-green-300", ring: "ring-green-400" },
                  };
                  return (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="users-stats-cards">
                      {cards.map((c) => {
                        const isActive = userRoleFilter === c.id;
                        const a = accentMap[c.accent];
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => setUserRoleFilter(isActive ? "all" : c.id)}
                            data-testid={`users-stats-card-${c.id}`}
                            title={`Filtrar por ${c.label}`}
                            className={`text-left rounded-2xl bg-ag7-surface border ${a.border} p-4 transition-all hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 ${a.ring} ${
                              isActive ? `ring-2 ${a.ring} shadow-md shadow-current` : ""
                            }`}
                          >
                            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 flex items-center justify-between">
                              <span>{c.label}</span>
                              {isActive && <span className={`text-[9px] ${a.text}`}>● activo</span>}
                            </p>
                            <p className={`text-2xl sm:text-3xl font-black font-mono tabular-nums ${a.text}`}>{c.value}</p>
                            <p className="text-[10px] text-zinc-500 font-mono mt-1">{c.sub}</p>
                          </button>
                        );
                      })}
                      {admins > 0 && (
                        <div className="col-span-2 lg:col-span-4 text-[10px] font-mono text-zinc-500 text-right">
                          + {admins} administrador{admins > 1 ? "es" : ""} (no incluidos en clientes/mayoristas)
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Search bar (username / phone) */}
                <div className="flex items-center gap-2 rounded-2xl bg-ag7-surface border border-[var(--ag7-border-soft)] p-3">
                  <Search className="w-4 h-4 text-zinc-500 flex-shrink-0 ml-1" />
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Buscar por usuario, nombre o teléfono (ej. kei45 o 04129...)"
                    data-testid="admin-user-search-input"
                    className="w-full bg-transparent border-0 outline-none text-white text-xs font-mono placeholder-zinc-600"
                  />
                  {userSearch && (
                    <button
                      onClick={() => setUserSearch("")}
                      data-testid="admin-user-search-clear"
                      className="text-zinc-500 hover:text-white text-xs px-2"
                    >
                      Limpiar
                    </button>
                  )}
                </div>

                {loadingUsers ? (
                  <div className="text-center py-12"><RefreshCw className="w-6 h-6 text-purple-400 animate-spin mx-auto" /></div>
                ) : (() => {
                  // Apply role filter + free-text search (username / name / email / phone)
                  const now = Date.now();
                  const in30d = 30 * 24 * 60 * 60 * 1000;
                  const q = userSearch.trim().toLowerCase();
                  const qDigits = q.replace(/\D/g, "");
                  const filteredUsers = users?.filter((u) => {
                    if (userRoleFilter === "client" && u.role !== "client") return false;
                    if (userRoleFilter === "reseller" && u.role !== "reseller") return false;
                    if (userRoleFilter === "new30d") {
                      const ts = u.created_at ? Date.parse(u.created_at) : 0;
                      if (!ts || now - ts >= in30d) return false;
                    }
                    if (!q) return true;
                    const hay = [
                      u.username || "",
                      u.name || "",
                      u.email || "",
                      u.phone_number || "",
                    ].join(" ").toLowerCase();
                    if (hay.includes(q)) return true;
                    if (qDigits && (u.phone_number || "").replace(/\D/g, "").includes(qDigits)) return true;
                    return false;
                  });
                  return (
                  <div className="rounded-2xl bg-ag7-surface border border-[var(--ag7-border-soft)] overflow-hidden">
                    <div className="px-4 py-2 text-[11px] font-mono text-zinc-500 border-b border-[var(--ag7-border)] flex items-center justify-between">
                      <span data-testid="users-filter-result-count">
                        Mostrando <strong className="text-white">{filteredUsers.length}</strong> de {users.length}
                        {userRoleFilter !== "all" && ` · filtro: ${userRoleFilter}`}
                        {q && ` · búsqueda: "${userSearch}"`}
                      </span>
                      {(userRoleFilter !== "all" || q) && (
                        <button
                          onClick={() => { setUserRoleFilter("all"); setUserSearch(""); }}
                          data-testid="admin-clear-user-filter"
                          className="text-cyan-400 hover:text-cyan-300 text-[11px] underline"
                        >
                          Limpiar filtros
                        </button>
                      )}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs" data-testid="users-table">
                        <thead>
                          <tr className="border-b border-[var(--ag7-border)] bg-ag7-bg/60 text-zinc-400 uppercase font-mono">
                            <th className="text-left px-4 py-3">Usuario</th>
                            <th className="text-left px-4 py-3">Rol</th>
                            <th className="text-left px-4 py-3">Estado</th>
                            <th className="text-right px-4 py-3">Pedidos</th>
                            <th className="text-right px-4 py-3">Gastado</th>
                            <th className="text-right px-4 py-3">Saldo</th>
                            <th className="text-left px-4 py-3">Registro</th>
                            <th className="text-right px-4 py-3">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredUsers.length === 0 ? (
                            <tr><td colSpan={8} className="text-center py-8 text-zinc-500">
                              {users.length === 0 ? "Sin usuarios" : "Ningún usuario coincide con los filtros"}
                            </td></tr>
                          ) : filteredUsers?.map(u => (
                            <tr key={u.id} className="border-b border-[var(--ag7-border)]/60 hover:bg-ag7-surface-2/40" data-testid={`user-row-${u.id}`}>
                              <td className="px-4 py-3">
                                <p className="font-bold text-white">{u.name}</p>
                                <p className="text-[11px] text-zinc-500 font-mono">{u.email}</p>
                                {u.phone_number && (
                                  <p
                                    className="text-[11px] text-cyan-400/90 font-mono flex items-center gap-1 mt-0.5"
                                    data-testid={`user-phone-${u.id}`}
                                  >
                                    <MessageCircle className="w-2.5 h-2.5" />
                                    {u.phone_number}
                                  </p>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {u.role === "admin" ? (
                                  <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 text-[10px] font-bold">ADMIN</span>
                                ) : u.role === "reseller" ? (
                                  <span data-testid={`user-role-badge-${u.id}`} className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-bold">REVENDEDOR</span>
                                ) : (
                                  <span className="text-zinc-400 text-[11px]">Cliente</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <UserStatusBadge status={u.status} />
                                {u.status_reason && (
                                  <p className="text-[10px] text-zinc-400 italic mt-1 max-w-[180px] truncate" title={u.status_reason} data-testid={`reason-${u.id}`}>
                                    💬 {u.status_reason}
                                  </p>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right font-mono">
                                <span className="text-white">{u.total_orders}</span>
                                <span className="text-green-400 text-[10px]"> / {u.completed_orders} ✓</span>
                              </td>
                              <td className="px-4 py-3 text-right font-mono text-cyan-400">Bs {bsAmount(u.total_spent_bs)}</td>
                              <td className="px-4 py-3 text-right font-mono text-purple-300">Bs {bsAmount(u.credit_balance_bs)}</td>
                              <td className="px-4 py-3 text-zinc-400 text-[11px] font-mono">
                                {u.created_at ? new Date(u.created_at).toLocaleDateString("es-VE") : "-"}
                              </td>
                              <td className="px-4 py-3">
                                {u.role === "admin" ? (
                                  <span className="text-[10px] text-zinc-600 italic">protegido</span>
                                ) : (
                                  <div className="flex items-center gap-1 justify-end flex-wrap">
                                    <button
                                      onClick={() => handleToggleUserRole(u)}
                                      data-testid={`toggle-role-user-${u.id}`}
                                      title={u.role === "reseller" ? "Volver a Cliente" : "Marcar como Revendedor"}
                                      className={`p-1.5 rounded-lg border ${u.role === "reseller" ? "bg-amber-950/60 hover:bg-amber-900 text-amber-300 border-amber-500/40" : "bg-ag7-surface-2 hover:bg-zinc-700 text-zinc-300 border-zinc-700"}`}
                                    >
                                      <DollarSign className="w-3.5 h-3.5" />
                                    </button>
                                    {u.status !== "active" && (
                                      <button onClick={() => handleChangeUserStatus(u.id, "active")} data-testid={`activate-user-${u.id}`}
                                        title="Reactivar" className="p-1.5 rounded-lg bg-green-950/60 hover:bg-green-900 text-green-300">
                                        <Check className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    {u.status !== "suspended" && (
                                      <button onClick={() => requestStatusChange(u, "suspended")} data-testid={`suspend-user-${u.id}`}
                                        title="Suspender (con motivo)" className="p-1.5 rounded-lg bg-yellow-950/60 hover:bg-yellow-900 text-yellow-300">
                                        <Clock className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    {u.status !== "blocked" && (
                                      <button onClick={() => requestStatusChange(u, "blocked")} data-testid={`block-user-${u.id}`}
                                        title="Bloquear (con motivo)" className="p-1.5 rounded-lg bg-red-950/60 hover:bg-red-900 text-red-300">
                                        <Lock className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    <button onClick={() => setDeleteUserConfirm(u)} data-testid={`delete-user-${u.id}`}
                                      title="Eliminar" className="p-1.5 rounded-lg bg-red-950/80 hover:bg-red-800 text-red-200 border border-red-500/40">
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  );
                })()}
              </div>
  );
}
